import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPasswordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)            score++;
  if (pw.length >= 12)           score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  return score; // 0–5
}

const STRENGTH_LABEL = ['', 'Weak', 'Fair', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLOR = ['', '#c0392b', '#c4722a', '#c4722a', '#2a7a6f', '#1e5c54'];

function friendlyError(msg) {
  if (!msg) return 'Something went wrong. Please try again.';
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid_credentials'))
    return 'Incorrect email or password.';
  if (m.includes('email not confirmed'))
    return 'Please confirm your email first — check your inbox.';
  if (m.includes('already registered') || m.includes('user already registered'))
    return 'An account with this email already exists.';
  if (m.includes('password should be') || m.includes('password is too short'))
    return 'Password must be at least 6 characters.';
  if (m.includes('rate limit') || m.includes('too many requests'))
    return 'Too many attempts. Please wait a moment and try again.';
  if (m.includes('unable to validate') || m.includes('invalid email'))
    return 'Please enter a valid email address.';
  return msg;
}

function validateEmail(val) {
  if (!val) return 'Email is required';
  if (!/\S+@\S+\.\S+/.test(val)) return 'Enter a valid email address';
  return null;
}

function validatePassword(val) {
  if (!val) return 'Password is required';
  if (val.length < 6) return 'At least 6 characters required';
  return null;
}

// ─── Eye icon ─────────────────────────────────────────────────────────────────

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M1 9s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M1 9s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2 2l14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuthScreen({ onSuccess, onSkip }) {
  const [mode, setMode]                   = useState('signup'); // signup | signin | forgot
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [rememberMe, setRememberMe]       = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [fieldErrors, setFieldErrors]     = useState({});
  const [touched, setTouched]             = useState({});
  const [sent, setSent]                   = useState(false);
  const [existingEmail, setExistingEmail] = useState(null); // existing-account nudge on signin

  const strength = mode === 'signup' ? getPasswordStrength(password) : 0;

  // Pre-fill remembered email when switching to sign-in
  useEffect(() => {
    if (mode === 'signin') {
      try {
        const remembered = localStorage.getItem('cp_remembered_email');
        if (remembered && !email) { setEmail(remembered); setRememberMe(true); }
      } catch {}
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  function switchMode(newMode) {
    setMode(newMode);
    setError(null);
    setFieldErrors({});
    setTouched({});
    setSent(false);
    setExistingEmail(null);
    setShowPassword(false);
    setShowConfirm(false);
  }

  function handleBlur(field) {
    setTouched(t => ({ ...t, [field]: true }));
    let err = null;
    if (field === 'email')           err = validateEmail(email);
    if (field === 'password')        err = validatePassword(password);
    if (field === 'confirmPassword') err = password !== confirmPassword ? "Passwords don't match" : null;
    setFieldErrors(fe => ({ ...fe, [field]: err }));
  }

  function handleEmailChange(val) {
    setEmail(val);
    if (touched.email) setFieldErrors(fe => ({ ...fe, email: validateEmail(val) }));
  }

  function handlePasswordChange(val) {
    setPassword(val);
    if (touched.password) setFieldErrors(fe => ({ ...fe, password: validatePassword(val) }));
    if (touched.confirmPassword)
      setFieldErrors(fe => ({ ...fe, confirmPassword: val !== confirmPassword ? "Passwords don't match" : null }));
  }

  function handleConfirmChange(val) {
    setConfirmPassword(val);
    if (touched.confirmPassword)
      setFieldErrors(fe => ({ ...fe, confirmPassword: val !== password ? "Passwords don't match" : null }));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  async function handleSubmit() {
    // Mark all relevant fields touched to show inline errors
    const newTouched = { email: true, password: mode !== 'forgot', confirmPassword: mode === 'signup' };
    setTouched(newTouched);

    const emailErr   = validateEmail(email);
    const pwErr      = mode !== 'forgot' ? validatePassword(password) : null;
    const confirmErr = mode === 'signup' && password !== confirmPassword ? "Passwords don't match" : null;
    setFieldErrors({ email: emailErr, password: pwErr, confirmPassword: confirmErr });
    if (emailErr || pwErr || confirmErr) return;

    if (mode === 'signup' && !agreedToTerms) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        // Detect existing account — Supabase returns user with empty identities array
        if (data.user?.identities?.length === 0) {
          setExistingEmail(email);
          switchMode('signin');
          return;
        }
        setSent(true);

      } else if (mode === 'signin') {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        if (rememberMe) {
          try { localStorage.setItem('cp_remembered_email', email); } catch {}
        } else {
          try { localStorage.removeItem('cp_remembered_email'); } catch {}
        }
        onSuccess();

      } else if (mode === 'forgot') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        setSent(true);
      }
    } catch (e) {
      setError(friendlyError(e.message));
    } finally {
      setLoading(false);
    }
  }

  // ── Confirmation: signup email sent ──
  if (sent && mode === 'signup') {
    return (
      <div style={s.screen}>
        <div style={s.card}>
          <div style={s.sentIcon}>✉️</div>
          <div style={s.sentTitle}>Check your email</div>
          <p style={s.sentText}>
            We sent a confirmation link to <strong>{email}</strong>.
            Click it to activate your account, then come back and sign in.
          </p>
          <button style={s.primaryBtn} onClick={() => switchMode('signin')}>Sign in</button>
          <button style={s.skipBtn} onClick={onSkip}>Skip for now</button>
        </div>
      </div>
    );
  }

  // ── Confirmation: password reset email sent ──
  if (sent && mode === 'forgot') {
    return (
      <div style={s.screen}>
        <div style={s.card}>
          <div style={s.sentIcon}>🔑</div>
          <div style={s.sentTitle}>Reset link sent</div>
          <p style={s.sentText}>
            We sent a password reset link to <strong>{email}</strong>. Check your inbox and follow the link.
          </p>
          <button style={s.primaryBtn} onClick={() => switchMode('signin')}>Back to sign in</button>
          <button style={s.skipBtn} onClick={onSkip}>Skip for now</button>
        </div>
      </div>
    );
  }

  // ── Forgot password mode ──
  if (mode === 'forgot') {
    return (
      <div style={s.screen}>
        <div style={s.card}>
          <div style={s.wordmark}>
            <span style={s.wordmarkBold}>Clear</span>
            <span style={s.wordmarkLight}>path</span>
          </div>

          <div style={s.heading}>Reset password</div>
          <p style={s.subheading}>Enter your email and we'll send you a link to reset your password.</p>

          {error && <div style={s.errorBox}>{error}</div>}

          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>Email</div>
            <input
              style={{ ...s.input, ...(fieldErrors.email && touched.email ? s.inputError : {}) }}
              type="email"
              value={email}
              onChange={e => handleEmailChange(e.target.value)}
              onBlur={() => handleBlur('email')}
              onKeyDown={handleKeyDown}
              placeholder="you@email.com"
              autoComplete="email"
            />
            {fieldErrors.email && touched.email && <div style={s.fieldError}>{fieldErrors.email}</div>}
          </div>

          <button
            style={{ ...s.primaryBtn, opacity: loading ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={loading || !email}
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </button>

          <button style={s.switchBtn} onClick={() => switchMode('signin')}>Back to sign in</button>
          <button style={s.skipBtn} onClick={onSkip}>Skip for now</button>
        </div>
      </div>
    );
  }

  // ── Sign up / Sign in ──
  return (
    <div style={s.screen}>
      <div style={s.card}>
        <div style={s.wordmark}>
          <span style={s.wordmarkBold}>Clear</span>
          <span style={s.wordmarkLight}>path</span>
        </div>

        <div style={s.heading}>
          {mode === 'signup' ? 'Save your progress' : 'Welcome back'}
        </div>
        <p style={s.subheading}>
          {mode === 'signup'
            ? 'Create a free account to save your insurance info and symptom history.'
            : 'Sign in to access your saved information.'}
        </p>

        {/* Existing account nudge (smart routing) */}
        {existingEmail && mode === 'signin' && (
          <div style={s.infoBox}>
            An account for <strong>{existingEmail}</strong> already exists. Sign in below.
          </div>
        )}

        {error && (
          <div style={s.errorBox}>
            {error}
            {error.includes('Incorrect email or password') && (
              <button style={s.errorLink} onClick={() => switchMode('forgot')}>
                Forgot your password?
              </button>
            )}
          </div>
        )}

        {/* Email */}
        <div style={s.fieldGroup}>
          <div style={s.fieldLabel}>Email</div>
          <input
            style={{ ...s.input, ...(fieldErrors.email && touched.email ? s.inputError : {}) }}
            type="email"
            value={email}
            onChange={e => handleEmailChange(e.target.value)}
            onBlur={() => handleBlur('email')}
            onKeyDown={handleKeyDown}
            placeholder="you@email.com"
            autoComplete="email"
          />
          {fieldErrors.email && touched.email && <div style={s.fieldError}>{fieldErrors.email}</div>}
        </div>

        {/* Password */}
        <div style={s.fieldGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={s.fieldLabel}>Password</div>
            {mode === 'signin' && (
              <button style={s.forgotLink} onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            )}
          </div>
          <div style={s.passwordWrap}>
            <input
              style={{ ...s.input, ...s.passwordInput, ...(fieldErrors.password && touched.password ? s.inputError : {}) }}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => handlePasswordChange(e.target.value)}
              onBlur={() => handleBlur('password')}
              onKeyDown={handleKeyDown}
              placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
            <button style={s.eyeBtn} onClick={() => setShowPassword(v => !v)} tabIndex={-1} type="button">
              <EyeIcon visible={showPassword} />
            </button>
          </div>
          {fieldErrors.password && touched.password && <div style={s.fieldError}>{fieldErrors.password}</div>}

          {/* Password strength — signup only */}
          {mode === 'signup' && password.length > 0 && (
            <div style={s.strengthWrap}>
              <div style={s.strengthBars}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} style={{ ...s.strengthBar, background: i <= strength ? STRENGTH_COLOR[strength] : 'var(--border)' }} />
                ))}
              </div>
              <div style={{ ...s.strengthLabel, color: STRENGTH_COLOR[strength] }}>
                {STRENGTH_LABEL[strength]}
              </div>
            </div>
          )}
        </div>

        {/* Confirm password — signup only */}
        {mode === 'signup' && (
          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>Confirm password</div>
            <div style={s.passwordWrap}>
              <input
                style={{ ...s.input, ...s.passwordInput, ...(fieldErrors.confirmPassword && touched.confirmPassword ? s.inputError : {}) }}
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => handleConfirmChange(e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                onKeyDown={handleKeyDown}
                placeholder="Re-enter your password"
                autoComplete="new-password"
              />
              <button style={s.eyeBtn} onClick={() => setShowConfirm(v => !v)} tabIndex={-1} type="button">
                <EyeIcon visible={showConfirm} />
              </button>
            </div>
            {fieldErrors.confirmPassword && touched.confirmPassword && (
              <div style={s.fieldError}>{fieldErrors.confirmPassword}</div>
            )}
          </div>
        )}

        {/* Terms checkbox — signup only */}
        {mode === 'signup' && (
          <label style={s.checkRow}>
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={e => setAgreedToTerms(e.target.checked)}
              style={s.checkbox}
            />
            <span style={s.checkLabel}>
              I agree to the{' '}
              <span style={s.checkLink}>Terms of Service</span>
              {' '}and{' '}
              <span style={s.checkLink}>Privacy Policy</span>
            </span>
          </label>
        )}

        {/* Remember Me — signin only */}
        {mode === 'signin' && (
          <label style={s.checkRow}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              style={s.checkbox}
            />
            <span style={s.checkLabel}>Remember me</span>
          </label>
        )}

        <button
          style={{ ...s.primaryBtn, opacity: loading ? 0.6 : 1 }}
          onClick={handleSubmit}
          disabled={loading || !email || !password}
        >
          {loading ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
        </button>

        <button
          style={s.switchBtn}
          onClick={() => switchMode(mode === 'signup' ? 'signin' : 'signup')}
        >
          {mode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
        </button>

        <button style={s.skipBtn} onClick={onSkip}>Skip for now</button>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  screen:       { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: '24px', overflowY: 'auto' },
  card:         { width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '14px' },

  wordmark:     { display: 'flex', alignItems: 'baseline', gap: '2px', marginBottom: '8px' },
  wordmarkBold: { fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '22px', color: 'var(--teal)', letterSpacing: '-0.5px' },
  wordmarkLight:{ fontFamily: 'var(--font-display)', fontWeight: 300, fontStyle: 'italic', fontSize: '22px', color: 'var(--ink-2)', letterSpacing: '-0.3px' },

  heading:      { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px' },
  subheading:   { fontSize: '13.5px', color: 'var(--ink-3)', lineHeight: 1.5, margin: 0 },

  errorBox:     { padding: '12px 16px', background: '#fef0f0', border: '1px solid #f5c6c6', borderRadius: 'var(--radius-md)', fontSize: '13px', color: '#c0392b', display: 'flex', flexDirection: 'column', gap: '6px' },
  errorLink:    { background: 'none', border: 'none', color: '#c0392b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline', textAlign: 'left', fontFamily: 'var(--font-body)' },
  infoBox:      { padding: '12px 16px', background: 'var(--teal-faint)', border: '1px solid rgba(30,92,84,0.2)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--teal)', lineHeight: 1.5 },

  fieldGroup:   { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel:   { fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' },
  fieldError:   { fontSize: '12px', color: '#c0392b', fontWeight: 500 },

  input:        { width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--surface)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  inputError:   { borderColor: '#e88080' },
  passwordWrap: { position: 'relative' },
  passwordInput:{ paddingRight: '44px' },
  eyeBtn:       { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: '4px', display: 'flex', alignItems: 'center' },

  forgotLink:   { background: 'none', border: 'none', fontSize: '12px', color: 'var(--teal)', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0, fontWeight: 600 },

  strengthWrap: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' },
  strengthBars: { display: 'flex', gap: '3px', flex: 1 },
  strengthBar:  { flex: 1, height: '3px', borderRadius: '2px', transition: 'background 0.2s' },
  strengthLabel:{ fontSize: '11px', fontWeight: 600, width: '36px', textAlign: 'right', flexShrink: 0 },

  checkRow:     { display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' },
  checkbox:     { marginTop: '2px', flexShrink: 0, accentColor: 'var(--teal)', width: '15px', height: '15px', cursor: 'pointer' },
  checkLabel:   { fontSize: '13px', color: 'var(--ink-3)', lineHeight: 1.5 },
  checkLink:    { color: 'var(--teal)', fontWeight: 600 },

  primaryBtn:   { width: '100%', padding: '15px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--teal)', color: 'white', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
  switchBtn:    { background: 'none', border: 'none', fontSize: '13px', color: 'var(--teal)', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center' },
  skipBtn:      { background: 'none', border: 'none', fontSize: '12px', color: 'var(--ink-4)', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center' },

  sentIcon:     { fontSize: '40px', textAlign: 'center' },
  sentTitle:    { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '22px', color: 'var(--ink)', textAlign: 'center' },
  sentText:     { fontSize: '13.5px', color: 'var(--ink-2)', lineHeight: 1.6, textAlign: 'center', margin: 0 },
};
